import React from 'react';
import { useScreenViewEvent, ScreenViewEvent } from '@stitch-fix/event-reporter';
import { useOnboardingContext } from './OnboardingContext';
import { OnboardingFlow } from './OnboardingFlow';
import { type OnboardingStep } from './steps/steps';
import Intro from './steps/Intro';
import OnboardingSelfieOnly from './steps/SelfieOnly';
import { useGetClientSelfieQuery } from './graphql/getClientSelfie';
import { SupportedBusinessLine } from './Onboarding';
import { Video } from '../../../types/definitions/vendor/graphql-schema.d';
import { SCREEN_VIEW_MAP } from './constants';
export { SCREEN_VIEW_MAP };

// Separate component for event tracking to avoid calling hook when not needed
const StepEventTracker = ({
  step,
  children
}: {
  step: keyof typeof SCREEN_VIEW_MAP;
  children: React.ReactNode;
}) => {
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'screen_view' as const,
    name: SCREEN_VIEW_MAP[step] as ScreenViewEvent['name'],
    source_app: 'categories-next-ui' as const,
    content_id: 'vision-onboarding-improvements-may-2026'
  });
  return <EventProvider>{children}</EventProvider>;
};
export const OnboardingContainer = ({
  businessLine,
  video
}: {
  businessLine: SupportedBusinessLine;
  video?: Video;
}) => {
  const {
    step,
    next,
    back,
    isCompleting
  } = useOnboardingContext();
  const currentStep = step as OnboardingStep;
  const {
    data: selfieData,
    refetch: refetchSelfie
  } = useGetClientSelfieQuery();
  const selfieImageUrl = selfieData?.client?.selfieImageUrl || null;
  const hasSelfieImage = Boolean(selfieImageUrl);
  const shouldTrackHere = currentStep in SCREEN_VIEW_MAP;
  const isNextButtonDisabled = () => {
    if (isCompleting) {
      return true;
    }
    if (currentStep === 'selfie') {
      return !hasSelfieImage;
    }
    return false;
  };
  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <Intro video={video} onNext={next} />;
      case 'selfie':
        return <OnboardingSelfieOnly selfieImageUrl={selfieImageUrl} businessLine={businessLine} onSelfieChange={() => refetchSelfie()} hideInlineUploadActionsWhenEmpty />;
      default:
        return null;
    }
  };
  const isSelfieOnlyWithoutImage = currentStep === 'selfie' && !selfieImageUrl;
  const content = <OnboardingFlow currentStep={currentStep} onNext={next} onBack={back} disabled={isNextButtonDisabled()} hideFooter={isSelfieOnlyWithoutImage}>
      {renderStep()}
    </OnboardingFlow>;
  if (shouldTrackHere) {
    return <StepEventTracker step={currentStep as keyof typeof SCREEN_VIEW_MAP}>
        {content}
      </StepEventTracker>;
  }
  return content;
};